export interface ConsentStatusItemApiResource {
  document_id: string;
  code: string;
  title: string;
  body: string;
  is_required: boolean;
  current_version_id: string;
  current_version: number;
  decision: string | null;
  decided_at: string | null;
  needs_reacceptance: boolean;
}

export interface ConsentStatusItem {
  documentId: string;
  code: string;
  title: string;
  body: string;
  isRequired: boolean;
  currentVersionId: string;
  currentVersion: number;
  decision: string | null;
  decidedAt: string | null;
  needsReacceptance: boolean;
}

export interface ConsentCaptureItem {
  documentId: string;
  documentVersionId: string;
  decision: string;
}

export const CONSENT_OVERVIEW_FILTER = {
  all: 'all',
  needsReacceptance: 'needs_reacceptance',
  pending: 'pending',
} as const;

export type ConsentOverviewFilter =
  (typeof CONSENT_OVERVIEW_FILTER)[keyof typeof CONSENT_OVERVIEW_FILTER];

export interface ConsentOverviewColumnApiResource {
  document_id: string;
  code: string;
  title: string;
  is_required: boolean;
}

export interface ConsentOverviewCellApiResource {
  document_id: string;
  code: string;
  decision: string | null;
  decided_at: string | null;
  needs_reacceptance: boolean;
}

export interface ConsentOverviewRowApiResource {
  client_id: string;
  client_display_name: string;
  has_pending: boolean;
  has_reacceptance: boolean;
  cells: ConsentOverviewCellApiResource[];
}

export interface ConsentOverviewApiResponse {
  columns: ConsentOverviewColumnApiResource[];
  items: ConsentOverviewRowApiResource[];
  page: number;
  page_size: number;
  total_items: number;
}

export interface ConsentOverviewColumn {
  documentId: string;
  code: string;
  title: string;
  isRequired: boolean;
}

export interface ConsentOverviewCell {
  documentId: string;
  code: string;
  decision: string | null;
  decidedAt: string | null;
  needsReacceptance: boolean;
}

export interface ConsentOverviewRow {
  clientId: string;
  clientDisplayName: string;
  hasPending: boolean;
  hasReacceptance: boolean;
  cells: ConsentOverviewCell[];
}

export interface ConsentOverview {
  columns: ConsentOverviewColumn[];
  rows: ConsentOverviewRow[];
  page: number;
  pageSize: number;
  totalItems: number;
}

function mapApiResourceToOverviewColumn(
  resource: ConsentOverviewColumnApiResource
): ConsentOverviewColumn {
  return {
    documentId: resource.document_id,
    code: resource.code,
    title: resource.title,
    isRequired: resource.is_required,
  };
}

function mapApiResourceToOverviewCell(
  resource: ConsentOverviewCellApiResource
): ConsentOverviewCell {
  return {
    documentId: resource.document_id,
    code: resource.code,
    decision: resource.decision,
    decidedAt: resource.decided_at,
    needsReacceptance: resource.needs_reacceptance,
  };
}

function mapApiResourceToOverviewRow(
  resource: ConsentOverviewRowApiResource
): ConsentOverviewRow {
  return {
    clientId: resource.client_id,
    clientDisplayName: resource.client_display_name,
    hasPending: resource.has_pending,
    hasReacceptance: resource.has_reacceptance,
    cells: resource.cells.map(mapApiResourceToOverviewCell),
  };
}

export function mapApiResourceToConsentOverview(
  resource: ConsentOverviewApiResponse
): ConsentOverview {
  return {
    columns: resource.columns.map(mapApiResourceToOverviewColumn),
    rows: resource.items.map(mapApiResourceToOverviewRow),
    page: resource.page,
    pageSize: resource.page_size,
    totalItems: resource.total_items,
  };
}

export function mapApiResourceToConsentItem(
  resource: ConsentStatusItemApiResource
): ConsentStatusItem {
  return {
    documentId: resource.document_id,
    code: resource.code,
    title: resource.title,
    body: resource.body,
    isRequired: resource.is_required,
    currentVersionId: resource.current_version_id,
    currentVersion: resource.current_version,
    decision: resource.decision,
    decidedAt: resource.decided_at,
    needsReacceptance: resource.needs_reacceptance,
  };
}
